import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import {
  TextField,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import LockResetIcon from "@mui/icons-material/LockReset";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { getAllTeamsApi, editTeamMemberApi, resendTeamMail } from "../../api/adminApi/adminApi";
import useEncryption from "../../services/useEncryption";
import EditTeamMemberModal from "../doctor/components/EditTeamMemberModal";
import ViewTeamMemberModal from "../doctor/components/ViewTeamMemberModal";
import { deleteTeamMemberApi } from "../../api/adminApi/adminApi";
import ChangeProviderModal from "./ChangeProviderModal";
import useHeaders from "../../helpers/useHeaders";
import useToast from "../../helpers/useToast";
const headCells = [
  { id: "name", label: "Name" },
  { id: "location", label: "Practice Location" },
  { id: "email", label: "Email" },
  { id: "status", label: "Status" },
  { id: "userType", label: "User Type" },
  { id: "actions", label: "Actions" },
];

export default function UserTable() {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("created_at");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All Leads");
  const { decryptData } = useEncryption();
  const providerId = null;
  const { id } = useParams();
  const [editOpen, setEditOpen] = useState(false); // modal open/close
  const [selectedEditUser, setSelectedEditUser] = useState(null); // data to edit
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedTeamMember, setSelectedTeamMember] = useState(null);
  const [selectedUserForLocationChange, setSelectedUserForLocationChange] = useState(null);
  const [changeProvider, setChangeProvider] = useState(false);
  const [update, setUpdate] = useState(false);
  const headers = useHeaders();
  const toastOptions = useToast();

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      fetchData();
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [page, rowsPerPage, order, orderBy, search, providerId, filter, update]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await getAllTeamsApi(
        page,
        rowsPerPage,
        orderBy,
        order,
        search
      );
      const decryptedData = decryptData(response?.data?.data);
      setUsers(decryptedData?.teams || []);
      setTotalUsers(decryptedData?.totalTeams || 0);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching data:", error);
      setUsers([]);
      setTotalUsers(0);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (member) => {
    setSelectedTeamMember(member);
    setViewOpen(true);
  };
  const handleEdit = (user) => {
    setSelectedEditUser(user); // set the selected user
    setEditOpen(true); // open the modal
  };

  const handleDelete = async (user) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete ${user.firstName} ${user.lastName}?`);

    if (!confirmDelete) return;

    try {
      const response = await deleteTeamMemberApi(user._id);
      toast.success(response?.data?.message || "User deleted successfully!");
      fetchData(); // Refresh the list
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete user");
    }
  };

  const handleChangeLocation = (user) => {
    // console.log(user);

    if (user.isPrimaryTrue) {
      toast.error("Updating Location for primary user is not allowed.");
      return;
    }
    setSelectedUserForLocationChange(user);
    setChangeProvider(true);
  }

  const handleResetPassword = async (user) => {
    try {
      let mailSent = await resendTeamMail(user?._id, headers);
      toast.success(mailSent.data.message);
    } catch (error) {
      toast.error(error.response.data.message, toastOptions);
    }
  }
  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleClose = () => {
    setSelectedUserForLocationChange(null);
    setEditOpen(false);
    setViewOpen(false);
    setChangeProvider(false);
  };
  
  return (
    <>
      {/* Edit Modal */}
      <EditTeamMemberModal
        open={editOpen}
        handleClose={() => setEditOpen(false)}
        selectedUser={selectedEditUser}
        refreshList={fetchData}
      />
      <ViewTeamMemberModal
        open={viewOpen}
        handleClose={() => setViewOpen(false)}
        teamMember={selectedTeamMember}
      />
      {/* Main User Table Layout */}
      <Box>
        {/* Search Bar */}
        <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search users..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            style={{ minWidth: 250 }}
          />
        </Box>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {headCells.map((cell) => (
                    <TableCell key={cell.id}>{cell.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id} 
                  sx={{ cursor: "pointer" }}
                  onClick={e => {
                    // Prevent click from action buttons or checkbox
                    if (
                      e.target.closest(".actionbuttons") ||
                      e.target.closest('input[type="checkbox"]')
                    ) {
                      return;
                    }
                    handleView(user);
                  }}>
                    <TableCell>{user.firstName || ""} {user.lastName || ""}</TableCell>
                    <TableCell>{user.organizationName || "-"}</TableCell>
                    <TableCell>{user.email || "-"}</TableCell>
                    <TableCell>{user.status || "-"}</TableCell>
                    <TableCell>{user.role || "-"}</TableCell>
                    <TableCell className="actionbuttons">
                      <Tooltip title="View">
                        <IconButton onClick={() => handleView(user)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleEdit(user)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={() => handleDelete(user)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Change Location">
                        <IconButton onClick={() => handleChangeLocation(user)}>
                          <SwapHorizIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset Password">
                        <IconButton onClick={() => handleResetPassword(user)}>
                          <LockResetIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={headCells.length} align="center">
                      No users found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />

          <ChangeProviderModal
            changeProvider={changeProvider}
            handleClose={handleClose}
            lead={selectedUserForLocationChange}
            setUpdate={setUpdate}
            type="user"
          />
        </Paper>
      </Box>
    </>
  );
}

